import { SagaIterator } from 'redux-saga';
import { all, call, put, select } from 'redux-saga/effects';
import { PerformanceMetric } from '../../../constants/telemetry';
import { ListDevBoxOperationsResponse } from '../../../data/services/data-plane-api/dev-box-operation';
import { ClientError, FailureOperation, isAggregatedFailure, isAggregatedSuccess } from '../../../models/common';
import { combineResults, getErrorCodes } from '../../../utilities/aggregated-result';
import { aggregateFailureResponses } from '../../../utilities/failure';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import {
    listDevBoxOperations,
    loadDevBoxOperationsForDevBoxes,
    loadDevBoxOperationsForDevBoxesError,
    loadDevBoxOperationsForDevBoxesFailed,
    loadDevBoxOperationsForDevBoxesSuccess,
} from '../../actions/dev-box-operation/dev-box-operation-action-creators';
import { LoadDevBoxOperationsForDevBoxesAction } from '../../actions/dev-box-operation/dev-box-operation-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { getIdsForDevBoxesRequiringDevBoxOperations } from './selectors';

export function* loadDevBoxOperationsForDevBoxesSaga(action: LoadDevBoxOperationsForDevBoxesAction): SagaIterator {
    const startTime = new Date();

    try {
        // Only do the load for devboxes that are in repairing or repaired state
        const ids: string[] = yield select(getIdsForDevBoxesRequiringDevBoxOperations);

        const responses: ListDevBoxOperationsResponse[] = yield all(
            ids.map((id) => putAndAwait(listDevBoxOperations({ id })))
        );

        const completeResult = combineResults(responses);

        if (isAggregatedFailure(completeResult)) {
            // All requests failed
            const { failures } = completeResult;
            const failure = aggregateFailureResponses(FailureOperation.LoadDevBoxOperationsForDevBoxes, ...failures);
            yield put(loadDevBoxOperationsForDevBoxesFailed({ failure }));
        } else {
            yield put(loadDevBoxOperationsForDevBoxesSuccess());
        }

        yield resolveAction(action, completeResult);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.LoadDevBoxOperationsForDevBoxes,
            startTime,
            completeResult.outcome,
            isAggregatedSuccess(completeResult) ? undefined : { errorCodes: getErrorCodes(completeResult) }
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.LoadDevBoxOperationsForDevBoxes);
        yield put(loadDevBoxOperationsForDevBoxesError({ error }));
        yield rejectAction(action, error);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.LoadDevBoxOperationsForDevBoxes,
            startTime,
            AsyncOutcome.Error,
            { errorCodes: [error.code] }
        );
    }
}

export function* loadDevBoxOperationsForDevBoxesListenerSaga(): SagaIterator {
    yield takeLeading(loadDevBoxOperationsForDevBoxes, loadDevBoxOperationsForDevBoxesSaga);
}
